import React, { useEffect, useState } from "react";
import axios from "axios";
import shared from "../styles/shared.module.css";
import styles from "../styles/AdminTickets.module.css";

type Usuario = {
  id: number;
  nome: string;
};

type TicketMessage = {
  id: number;
  conteudo: string;
  usuarioNome: string;
  enviadoEm: string;
  arquivoId?: number | null;
  arquivoUrl?: string | null;
};


type Ticket = {
  id: number;
  assunto: string;
  status: string;
  criadoEm: string;
  usuarioId: number;
  usuarioNome: string;
  naoLidas: number;
};

const AdminTickets = () => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [resposta, setResposta] = useState("");
  const [activeTab, setActiveTab] = useState<"ativos" | "fechados">("ativos");

  // Buscar tickets
  // Função para buscar tickets
  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/admins/tickets`, { headers });
      const sortedTickets = res.data.sort((a: Ticket, b: Ticket) => {
        if (a.naoLidas > 0 && b.naoLidas === 0) return -1;
        if (a.naoLidas === 0 && b.naoLidas > 0) return 1;
        return new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime();
      });
      setTickets(sortedTickets);
    } catch (err) {
      console.error("Erro ao carregar tickets", err);
    }
  };

  // useEffect inicial
  useEffect(() => {
    fetchTickets();
  }, []);

  // Voltar da conversa
  const handleBack = () => {
    setSelectedTicket(null);
    fetchTickets(); // ✅ Atualiza a lista para remover badge
  };

  // Buscar mensagens ao selecionar ticket
  const handleSelectTicket = async (ticket: Ticket) => {
    setSelectedTicket(ticket);
    try {
      const res = await axios.get(
        `${BASE_URL}/admins/tickets/${ticket.id}/messages`,
        { headers }
      );
      setMessages(res.data);
    } catch (err) {
      console.error("Erro ao carregar mensagens", err);
    }
  };

  // Enviar resposta
  const handleSendMessage = async () => {
    if (!selectedTicket || !resposta.trim()) return;

    const formData = new FormData();
    formData.append("conteudo", resposta);

    try {
      const res = await axios.post(
        `${BASE_URL}/admins/tickets/${selectedTicket.id}/reply`,
        formData,
        { headers }
      );

      setMessages([...messages, res.data]);
      setResposta("");
    } catch (err) {
      console.error("Erro ao enviar resposta", err);
    }
  };

  const [newStatus, setNewStatus] = useState(selectedTicket?.status);

  // sempre que trocar de ticket, resetar o status local
  useEffect(() => {
    if (selectedTicket) setNewStatus(selectedTicket.status);
  }, [selectedTicket]);

  // salvar no backend só quando clicar no botão
  const handleConfirmStatus = async () => {
    if (!selectedTicket) return;

    try {
      const res = await axios.put(
        `${BASE_URL}/admins/tickets/${selectedTicket.id}/status`,
        { status: newStatus },
        { headers }
      );

      setSelectedTicket(res.data);

      setTickets((prev) =>
        prev.map((t) => (t.id === res.data.id ? res.data : t))
      );
    } catch (err) {
      console.error("Erro ao alterar status", err);
    }
  };

  const filteredTickets = tickets.filter((t) =>
    activeTab === "ativos"
      ? t.status !== "FECHADO"
      : t.status === "FECHADO"
  );

  return (
    <div className={shared.container}>
      <h2 className={shared.title}>Tickets de Suporte</h2>

      {!selectedTicket ? (
        // Tela 1 - Lista de tickets
        <div className={styles.ticketListPage}>
          {/* Abas */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tabButton} ${activeTab === "ativos" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("ativos")}
            >
              Abertos / Em progresso
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === "fechados" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("fechados")}
            >
              Fechados
            </button>
          </div>

          {/* Lista de tickets */}
          <div className={styles.ticketList}>
            {filteredTickets.length === 0 ? (
              <p style={{ padding: "12px", color: "#666" }}>
                Nenhum ticket {activeTab === "ativos" ? "ativo" : "fechado"}.
              </p>
            ) : (
              filteredTickets.map((t) => (
                <div
                  key={t.id}
                  className={styles.ticketItem}
                  onClick={() => handleSelectTicket(t)}
                >
                  <div className={styles.ticketListHeader}>
                    <span>{t.assunto}</span>
                    {/* Badge de mensagens não lidas */}
                    {t.naoLidas > 0 && (
                      <span className={styles.unreadBadge}>{t.naoLidas}</span>
                    )}
                  </div>
                  <div className={styles.ticketMeta}>
                    <small>
                      {t.usuarioNome ?? "Usuário"} •{" "}
                      {new Date(t.criadoEm).toLocaleDateString()}
                    </small>
                    <span className={`${styles.statusBadge} ${styles[`status_${t.status}`]}`}>
                      {t.status === "ABERTO"
                        ? "Aberto"
                        : t.status === "EM_PROGRESSO"
                          ? "Em progresso"
                          : "Fechado"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        // Tela 2 - Conversa do ticket
        <div className={styles.ticketDetailPage}>
          <button className={styles.backButton} onClick={handleBack}>
            ← Voltar
          </button>

          <div className={styles.ticketDetailHeader}>
            <div>
              <strong>{selectedTicket.assunto}</strong>
              <div style={{ fontSize: "14px", marginTop: "4px" }}>
                Usuário: {selectedTicket.usuarioNome}
              </div>
            </div>

            {/* Área de status + botão */}
            <div className={styles.statusContainer}>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className={styles.statusSelect}
              >
                <option value="ABERTO">Aberto</option>
                <option value="EM_PROGRESSO">Em progresso</option>
                <option value="FECHADO">Fechado</option>
              </select>
              <button
                onClick={handleConfirmStatus}
                className={styles.confirmButton}
                disabled={newStatus === selectedTicket.status}
              >
                Confirmar
              </button>
            </div>
          </div>

          <div className={styles.messages}>
            {messages.map((m) => (
              <div
                key={m.id}
                className={`${styles.message} ${m.usuarioNome === selectedTicket.usuarioNome
                  ? styles.userMessage
                  : styles.adminMessage
                  }`}
              >
                <p>{m.conteudo}</p>

                {/* Anexo */}
                {m.arquivoUrl && (
                  <div style={{ marginTop: "5px" }}>
                    <a
                      href={`${BASE_URL}${m.arquivoUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", marginBottom: "5px" }}
                    >
                      📎 Ver anexo
                    </a>

                    {/* Preview de imagem genérico */}
                    <img
                      src={`${BASE_URL}${m.arquivoUrl}`}
                      alt="Anexo"
                      style={{
                        maxWidth: "200px",
                        borderRadius: "6px",
                        display: "block",
                        marginTop: "5px",
                      }}
                      onError={(e) => {
                        // Se não for imagem, esconde o preview
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}

                <small>
                  {m.usuarioNome} • {new Date(m.enviadoEm).toLocaleTimeString()}
                </small>
              </div>
            ))}
          </div>

          {/* Enviar resposta */}
          <div className={styles.responseBox}>
            <textarea
              value={resposta}
              onChange={(e) => setResposta(e.target.value)}
              placeholder="Digite sua resposta..."
            />
            <button onClick={handleSendMessage}>Responder</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTickets;
